import { isEssentialSafe } from '../config/parse';
import { emitDebug } from '../debug';
import type { ConsentConfig, ConsentState, ScriptConfig } from '../types';
import { injectHtml, ready } from '../utils/dom';

/** Nodes created per script, so a withdrawn permission can undo the injection. */
const injectedNodes = new Map<string, Node[]>();

export function resetInjectedScripts(): void {
  injectedNodes.clear();
}

/** Removes every node this runtime added and forgets them, used on config reload. */
export function clearInjectedScripts(): void {
  for (const id of Array.from(injectedNodes.keys())) {
    removeNodesFor(id);
  }
  injectedNodes.clear();
}

export function injectedScriptIds(): string[] {
  return Array.from(injectedNodes.keys());
}

export function injectEligibleScripts(
  scripts: ScriptConfig[],
  environment: string,
  consent: ConsentState | null,
  categories: ConsentConfig['categories'],
): void {
  const requiredIds = new Set(categories.filter((c) => c.required).map((c) => c.id));

  const candidates = scripts
    .filter((script) => script.enabled && script.content.trim())
    .filter((script) => script.environment === 'all' || script.environment === environment)
    .filter((script) => !injectedNodes.has(keyOf(script)));

  const eligible = candidates
    .filter((script) => isScriptEligible(script, consent, requiredIds))
    .sort((a, b) => a.priority - b.priority);

  for (const script of candidates) {
    if (eligible.includes(script)) continue;
    emitDebug('script_blocked', `${script.name} is waiting for consent`, {
      script: script.name,
      id: keyOf(script),
      requires: script.consent_categories,
      missing: missingCategories(script, consent, requiredIds),
    });
  }

  const run = () => {
    for (const script of eligible) {
      const key = keyOf(script);
      try {
        const nodes = injectHtml(script.position, script.content);
        injectedNodes.set(key, nodes);
        emitDebug('script_injected', `${script.name} fired`, {
          script: script.name,
          id: key,
          position: script.position,
          requires: script.consent_categories,
        });
      } catch {
        // Ignore malformed script content.
      }
    }
  };

  if (eligible.some((s) => s.position !== 'head')) {
    ready(run);
  } else {
    run();
  }
}

/**
 * Drops scripts that are no longer allowed by the current consent state.
 * Returns the names removed so callers can report or reload.
 */
export function removeIneligibleScripts(
  scripts: ScriptConfig[],
  consent: ConsentState | null,
  categories: ConsentConfig['categories'],
): string[] {
  const requiredIds = new Set(categories.filter((c) => c.required).map((c) => c.id));
  const removed: string[] = [];

  for (const script of scripts) {
    const key = keyOf(script);
    if (!injectedNodes.has(key)) continue;
    if (isScriptEligible(script, consent, requiredIds)) continue;

    removeNodesFor(key);
    injectedNodes.delete(key);
    removed.push(script.name);
    emitDebug('script_removed', `${script.name} was removed after consent was withdrawn`, {
      script: script.name,
      id: key,
      requires: script.consent_categories,
    });
  }

  return removed;
}

export function isScriptEligible(
  script: ScriptConfig,
  consent: ConsentState | null,
  requiredIds: Set<string>,
): boolean {
  if (isEssentialSafe(script, requiredIds)) {
    return true;
  }

  if (!consent) return false;

  // AND: every linked category must be granted.
  return script.consent_categories.every((id) => Boolean(consent.categories[id]));
}

export function missingCategories(
  script: ScriptConfig,
  consent: ConsentState | null,
  requiredIds: Set<string>,
): string[] {
  if (isEssentialSafe(script, requiredIds)) return [];
  if (!consent) return script.consent_categories.slice();

  return script.consent_categories.filter((id) => !consent.categories[id]);
}

function keyOf(script: ScriptConfig): string {
  return script.id || script.name;
}

function removeNodesFor(id: string): void {
  for (const node of injectedNodes.get(id) || []) {
    try {
      (node as ChildNode).remove?.();
    } catch {
      node.parentNode?.removeChild(node);
    }
  }
}
